<template>
  <div class="note-editor-bar electron-drag">
    <div
      class="left-side flex gap-2"
      :class="{ 'ai-note-generating': aiNoteGenerating }"
    >
      <ButtonCustom
        v-if="!noteHasAiNotes && !aiNoteGenerating && !props.isInboxNote"
        v-tooltip="{
          content: translate('enhanceNotesUsingAI'),
          placement: 'top',
        }"
        :class="{ 'disabled-button': isNewNote }"
        :text="translate('enhance')"
        :type="'secondary-regular secondary-button electron-no-drag'"
        :custom-icon-name="'zap'"
        :custom-icon-color="'--var(iconDefault)'"
        :custom-icon-size="'16px'"
        :button-dimensions="{ height: '24px' }"
        :disabled="isNewNote"
        @click="enhanceNote"
      />
      <Tabs
        v-if="(noteHasAiNotes && !isNewNote) || aiNoteGenerating"
        :items="noteTabItems"
        :dropdown-for-tab="NoteTabId.Enhanced"
        class="note-editor-tabs electron-no-drag"
        :class="{ 'ai-note-generating': aiNoteGenerating }"
        @click="noteTabClick"
      />
      <v-popover
        interactive
        tag="div"
        content-tag="div"
        :trigger="'click'"
        :role="'poper'"
        :placement="'bottom-start'"
        :append-to="bodyRef"
        :on-show="() => !isNewNote"
      >
        <ButtonCustom
          v-if="!isElectronQuickNote"
          :class="{ 'disabled-button': isNewNote }"
          :text="translate('convert')"
          :type="'secondary-regular secondary-button electron-no-drag'"
          :custom-icon-name="'arrow-right'"
          :custom-icon-color="'--var(iconDefault)'"
          :custom-icon-size="'16px'"
          :button-dimensions="{ height: '24px' }"
          :disabled="isNewNote"
        />
        <template #content="{ state, hide }">
          <ListDropdown
            v-if="state.isVisible"
            :width="240"
            :items="convertToOptions"
            @click="handleConvertToOptionClick($event, hide)"
            @closeEscape="hide"
          />
        </template>
      </v-popover>
    </div>
    <div class="right-side">
      <ButtonCustom
        v-if="showNoteMeetingLink"
        :text="joinNoteMeetingText"
        :type="'secondary-regular secondary-button electron-no-drag'"
        :custom-icon-name="meetingServiceIcon.name"
        :custom-icon-color="meetingServiceIcon.color"
        :custom-icon-size="'16px'"
        :custom-icon-use-mask="meetingServiceIcon.useMask"
        :custom-icon-props="meetingServiceIcon.styles"
        :button-dimensions="{ height: '24px' }"
        @click="openNoteMeetingLink"
      />
      <ButtonCustom
        v-if="!isQuickNote && !props.isInboxNote"
        :class="{ 'disabled-button': isNewNote }"
        :text="suggestedActionsButtonText"
        :type="'secondary-regular secondary-button electron-no-drag'"
        :button-dimensions="{ height: '24px' }"
        :disabled="isNewNote"
        @click="handleSuggestedActionsClick"
      >
        <div
          v-if="isGeneratingActions"
          class="flex gap-x-1 items-center"
        >
          <SuggestionSpinner />
          <span class="button-label"> {{ suggestedActionsButtonText }}</span>
        </div>
      </ButtonCustom>
      <v-popover
        interactive
        tag="div"
        content-tag="div"
        placement="bottom-end"
        class="p-0"
        :trigger="'click'"
        :role="'poper'"
      >
        <template #default>
          <ButtonCustom
            v-if="!isNewNote"
            :type="'share-secondary-regular transparent hover:bg-hover-onHover py-1 px-2 ml-auto electron-no-drag'"
            :button-dimensions="{ height: 24 }"
            :class="{ 'highlighted-share-button': publicNoteUrl }"
            :custom-icon-name="'globe'"
            :custom-icon-size="'16px'"
            :custom-icon-color="togglePublicNoteIconColor"
            :text="togglePublicNoteText"
          />
        </template>
        <template #content="{ state }">
          <TogglePublicNote
            v-if="state.isVisible"
            :url="publicNoteUrl"
            :type="ResourceType.Note"
            :standalone="true"
            @click="togglePublicNote(noteId)"
          />
        </template>
      </v-popover>
      <v-popover
        interactive
        tag="div"
        content-tag="div"
        :trigger="'click'"
        :role="'poper'"
        :placement="'bottom-start'"
      >
        <div
          v-if="!isNewNote && !isQuickNote"
          class="flex hover:bg-icon-bg w-5 h-5 rounded p-0.5 pt items-center justify-center electron-no-drag"
        >
          <em
            class="custom-icons"
            :style="getCustomIconStyle(iconPath('more-horizontal'))"
          />
        </div>
        <template #content="{ state, hide }">
          <ListDropdown
            v-if="state.isVisible"
            :width="240"
            :fixed-width="true"
            :items="noteOptions"
            @click="noteOptionClick($event, hide)"
            @closeEscape="hide"
          />
        </template>
      </v-popover>
      <v-popover
        v-if="isNewNote && !isQuickNote"
        placement="top"
      >
        <ButtonCustom
          :text="translate('cancel')"
          :type="'secondary-regular secondary-button electron-no-drag'"
          :custom-icon-name="'x'"
          :custom-icon-color="'--var(iconDefault)'"
          :custom-icon-size="'16px'"
          :button-dimensions="{ height: '24px' }"
          @click="closeNote"
        />
        <template #content>
          <TooltipTemplate
            :tooltip-text="translate('cancel')"
            :tooltip-meta="{ icon: { key: 'ESC' } }"
          />
        </template>
      </v-popover>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, toRef, getCurrentInstance, watch, onMounted, onBeforeUnmount } from 'vue';
import useTranslate from '@/utilities/composables/useTranslate';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import ButtonCustom from '@/components/elements/ButtonCustom';
import ListDropdown from '@/components/widgets/ListDropdown';
import useNoteOptionsDropdown from '@/utilities/composables/useNoteOptionsDropdown';
import EventBus from '@/utilities/eventBus';
import Tabs from '@/components/widgets/Tabs';
import useTabs from '@/utilities/composables/useTabs';
import { NoteTabId, TabDropdownOptions } from '@/types/tabs';
import { NoteTemplate, quickPageNoteKey } from '@/types/notes';
import { browserStorageSetItem } from '@/utilities/browser-storage.util';
import { ResourceType } from '@/types/resources';
import constants from '@/utilities/constants';
import getDashedTitle from '@/utilities/getDashedTitle';
import useNoteContent from '@/utilities/composables/useNoteContent';
import useNotes from '@/utilities/composables/useNotes';
import TooltipTemplate from '@/components/elements/TooltipTemplate';
import { convertDateToDDMM, convertToHoursMinutes } from '@/utilities/timeDifference';
import capitalizeFirstLetter from '@/utilities/capitalizeFirstLetter';
import TogglePublicNote from '@/components/notes/TogglePublicNote';
import SuggestionSpinner from '@/components/elements/SuggestionSpinner';
import { ActionStatus } from '@superthread-com/api';
import { useStore } from 'vuex';
import { globalState } from '@/utilities/globalState';
import useSuggestedActions from '@/utilities/composables/useSuggestedActions';
import { isToday } from 'date-fns';

import useAudioTranscription from '@/utilities/composables/useAudioTranscription';
import useIsElectronApp from '@/utilities/composables/useIsElectronApp';
import useEpicFeature from '@/utilities/composables/useEpicFeature';

const _this = getCurrentInstance()?.proxy as any;

const bodyRef = ref(document.body);

const selectedNoteTabId = ref(NoteTabId.Original);

const { translate } = useTranslate();
const { iconPath, getCustomIconStyle } = useCustomIcon();

enum MeetingServices {
  Zoom = 'zoom',
  Teams = 'teams',
  Webex = 'webex',
  GoogleMeet = 'g-meet',
}

type MeetingServiceIconProps = {
  name: string;
  color?: string;
  useMask?: boolean;
  styles?: Record<string, any>;
};

const meetingServiceIcons: Map<MeetingServices, MeetingServiceIconProps> = new Map([
  [
    MeetingServices.Zoom,
    {
      name: 'zoom',
      color: 'transparent',
      useMask: false,
      styles: { backgroundColor: 'transparent' },
    },
  ],
  [
    MeetingServices.Teams,
    {
      name: 'teams',
      color: 'transparent',
      useMask: false,
      styles: { backgroundColor: 'transparent' },
    },
  ],
  [
    MeetingServices.Webex,
    {
      name: 'webex',
      color: 'transparent',
      useMask: false,
      styles: {
        backgroundColor: 'transparent',
        transform: 'translateY(2px)',
      },
    },
  ],
  [
    MeetingServices.GoogleMeet,
    {
      name: 'g-meet',
      color: 'transparent',
      useMask: false,
      styles: { backgroundColor: 'transparent' },
    },
  ],
]);

const meetingServiceIcon = computed((): MeetingServiceIconProps => {
  const provider = noteMeetingLinkInfo.value.provider?.toLowerCase() || '';

  // Return the appropriate icon based on the meeting provider
  if (provider.includes('zoom')) return meetingServiceIcons.get(MeetingServices.Zoom)!;
  if (provider.includes('teams') || provider.includes('microsoft'))
    return meetingServiceIcons.get(MeetingServices.Teams)!;
  if (provider.includes('webex') || provider.includes('cisco'))
    return meetingServiceIcons.get(MeetingServices.Webex)!;
  if (provider.includes('meet') || provider.includes('google'))
    return meetingServiceIcons.get(MeetingServices.GoogleMeet)!;

  // Default fallback to video icon
  return {
    name: 'video-st',
    color: 'var(--iconDefault)',
    useMask: true,
  } as MeetingServiceIconProps;
});

const { getNoteTabs } = useTabs();
const {
  getAiNote,
  noteTemplates,
  getLastNoteTemplate,
  getNoteHasAiNotes,
  noteTemplatesFavourited,
  getPublicNoteUrl,
  togglePublicNote,
  getNoteMeetingLinkInfo,
} = useNotes();
const { actionTypes } = useSuggestedActions();
const store = useStore();
const { isElectronApp } = useIsElectronApp();
const { epicUpgradeBadge, allowEpicInteraction } = useEpicFeature();

const props = defineProps({
  noteId: {
    type: String,
    required: true,
  },
  isQuickNote: {
    type: Boolean,
    required: false,
    default: false,
  },
  isElectronQuickNote: {
    type: Boolean,
    required: false,
    default: false,
  },
  isInboxNote: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { isAutoStartTranscriptionEnabled, startRecording } = useAudioTranscription(
  toRef(props, 'noteId')
);

const { noteTemplateId, noteContent } = useNoteContent(toRef(props, 'noteId'));

const note = computed(() => {
  return _this.$store.getters.getNotes[props.noteId];
});

const isNewNote = computed(() => props.noteId === constants.newNote);

const { noteOptions, noteOptionClick } = useNoteOptionsDropdown(toRef(props, 'noteId'));

const templateDropdownOptions = computed(() => {
  return {
    items: noteTemplatesOptions.value,
    selectedItem: selectedTemplateIndex.value,
    checkmarkDisabled: true,
    searchPlaceholder: `${translate('templates')}...`,
    footerText: translate('manageTemplates'),
    clickFn: handleApplyTemplateOptionClick,
  } as TabDropdownOptions;
});

const noteTabItems = computed(() => {
  return getNoteTabs(
    selectedNoteTabId.value,
    selectedTemplateLabel.value,
    templateDropdownOptions.value
  );
});

const aiNoteGenerating = computed(() => {
  return _this.$store.getters.getAiNoteGenerating;
});

const aiNote = computed(() => getAiNote(props.noteId, noteTemplateId.value) ?? {});

const aiNoteActions = computed(() => aiNote.value.actions || []);

const pendingAiNoteActions = computed(() => {
  return aiNoteActions.value.filter(
    (action: any) =>
      action.status === ActionStatus.Pending &&
      [actionTypes.create_card, actionTypes.create_comment].includes(action.type)
  );
});

const numberOfSuggestions = computed(() => pendingAiNoteActions.value.length);

const isGeneratingActions = computed(() => aiNote.value.isGeneratingActions);

const getFavouritedTemplates = () => {
  return Object.entries(noteTemplatesFavourited.value).reduce<any[]>(
    (acc, [id, template]: [string, NoteTemplate], index, array) => {
      acc.push({
        id,
        resourceIcon: template.icon,
        label: translate(template.title),
        selected: id === noteTemplateId.value,
        shouldBeBold: id === noteTemplateId.value,
        ...(getAiNote(props.noteId, id) && {
          ...getTemplateIcons(template),
        }),
        type: ResourceType.NoteTemplate,
        groupLabel: translate('favouriteTemplates'),
        groupingItem: true,
        ...(index === array.length - 1 && { separator: true }),
      });
      return acc;
    },
    []
  );
};

const getUserCreatedTemplates = (currentTemplateIds: Set<string>) => {
  const arr = Object.entries(noteTemplates.value).reduce<any[]>(
    (acc, [id, template]: [string, any]) => {
      // templates that are created by current user but not favorited
      if (
        !template.is_favourited &&
        !currentTemplateIds.has(id) &&
        template.user?.user_id === store.getters.getUserId
      ) {
        acc.push({
          id,
          resourceIcon: template.icon,
          label: translate(template.title),
          selected: id === noteTemplateId.value,
          shouldBeBold: id === noteTemplateId.value,
          ...(getAiNote(props.noteId, id) && {
            ...getTemplateIcons(template),
          }),
          type: ResourceType.NoteTemplate,
          groupLabel: translate('myTemplates'),
          groupingItem: true,
        });
      }
      return acc;
    },
    []
  );

  if (arr.length > 0) {
    arr[arr.length - 1] = {
      ...arr[arr.length - 1],
      separator: true,
    };
  }

  return arr;
};

const getOtherTemplates = (currentTemplateIds: Set<string>) => {
  // templates that are not favorited and not created by current user and templates that have been used but are deleted
  const otherUsersTemplates = Object.entries(noteTemplates.value).reduce<any[]>(
    (acc, [id, template]: [string, any]) => {
      if (
        !template.is_favourited &&
        !currentTemplateIds.has(id) &&
        template.user?.user_id !== store.getters.getUserId
      ) {
        acc.push({
          id,
          resourceIcon: template.icon,
          label: translate(template.title),
          selected: id === noteTemplateId.value,
          shouldBeBold: id === noteTemplateId.value,
          ...(getAiNote(props.noteId, id) && {
            ...getTemplateIcons(template),
          }),
          type: ResourceType.NoteTemplate,
          ...getOtherTemplateGroup(),
        });
      }
      return acc;
    },
    []
  );

  const deletedTemplates = (note.value?.ai_notes || []).reduce((acc: any[], aiNote) => {
    if (
      !currentTemplateIds.has(aiNote.note_template_id) &&
      !noteTemplates.value[aiNote.note_template_id]
    ) {
      acc.push({
        id: aiNote.note_template_id,
        resourceIcon: {
          src: 'help-circle',
          color: 'var(--iconDefault)',
          type: 'icon',
        },
        label: `${capitalizeFirstLetter(translate('deletedNotification'))} ${convertDateToDDMM(aiNote.time_updated)}`,
        selected: aiNote.note_template_id === noteTemplateId.value,
        shouldBeBold: aiNote.note_template_id === noteTemplateId.value,
        ...(getAiNote(props.noteId, aiNote.note_template_id) && {
          ...getTemplateIcons({ id: aiNote.note_template_id }),
          type: ResourceType.NoteTemplate,
        }),
        ...getOtherTemplateGroup(),
      });
    }
    return acc;
  }, [] as any[]);

  const combinedTemplates = [...otherUsersTemplates, ...deletedTemplates];

  return combinedTemplates;
};

const getOtherTemplateGroup = () => {
  return {
    groupLabel: translate('otherTemplates'),
    groupingItem: true,
  };
};

const noteTemplatesOptions = computed(() => {
  const currentTemplateIds = new Set(Object.keys(noteTemplatesFavourited.value));

  const favouritedTemplates = getFavouritedTemplates();
  const userCreatedTemplates = getUserCreatedTemplates(currentTemplateIds);
  const otherTemplates = getOtherTemplates(currentTemplateIds);

  return [...favouritedTemplates, ...userCreatedTemplates, ...otherTemplates];
});

const noteHasAiNotes = computed(() => getNoteHasAiNotes(props.noteId));

const getTemplateIcons = (template) => {
  return {
    showCheck: true,
    ...((noteTemplates.value[template.id] || noteTemplatesFavourited.value[template.id]) && {
      additionalRightIcon: iconPath('refresh-cw'),
      additionalRightIconTooltip: translate('regenerate'),
      additionalRightIconCallback: () => applyTemplate(template, true, true),
    }),
  };
};

const closeNote = () => {
  EventBus.$emit('closeNewNote');
};

const fetchNoteTemplates = () => {
  _this.$store.dispatch('fetchNoteTemplates');
};

const showWarningToCreateNote = () => {
  if (isNewNote.value) {
    _this.$store.dispatch('addUiNotification', {
      message: translate('createNoteBeforeUsingQuickActions'),
      status: constants.uiNotificationStatuses.warning,
      duration: 4000,
    });
  }

  return isNewNote.value;
};

const enhanceNote = () => {
  noteTabClick(NoteTabId.Enhanced);
};

const noteTabClick = (tab, shouldUpdateLastNoteTemplateId = true) => {
  if (tab === NoteTabId.Original) closeSuggestedActionsSidebar();

  switchToTab(tab);

  const templateToSwitchTo = note.value?.last_note_template_id || NoteTemplate.AutoEnhanced;

  noteTemplateId.value = tab === NoteTabId.Enhanced ? templateToSwitchTo : '';

  const shouldCreateAiNote = !noteHasAiNotes.value && tab === NoteTabId.Enhanced;

  EventBus.$emit('note:tabChange', {
    noteId: props.noteId,
    templateId: noteTemplateId.value,
    shouldUpdateLastNoteTemplateId,
    shouldCreateAiNote,
  });
};

const switchToTab = (tab: NoteTabId) => {
  selectedNoteTabId.value = tab;
};

const applyTemplate = (
  template,
  shouldUpdateLastNoteTemplateId = true,
  forceRegenerate = false
) => {
  noteTemplateId.value = template?.id;
  selectedNoteTabId.value = NoteTabId.Enhanced;

  const selectedTemplateAiNote = getAiNote(props.noteId, template?.id);

  if (selectedTemplateAiNote) {
    store.dispatch('getNoteSuggestions', {
      noteId: props.noteId,
      aiNoteId: selectedTemplateAiNote.id,
    });
  }

  EventBus.$emit('note:applyTemplate', {
    templateId: template?.id,
    noteId: props.noteId,
    shouldUpdateLastNoteTemplateId,
    forceRegenerate,
  });
};

const handleApplyTemplateOptionClick = (template) => applyTemplate(template);

const convertNoteToCard = (noteTitle, noteContent) => {
  _this.$store.dispatch('setQuickCardField', {
    fieldName: 'title',
    value: noteTitle,
    isModal: true,
  });
  _this.$store.dispatch('setQuickCardField', {
    fieldName: 'content',
    value: noteContent,
    isModal: true,
  });
  _this.$store.dispatch('setCardInNoteCreation', note.value?.id);
  _this.$store.dispatch('setQuickCard', true);
};

const convertNoteToPage = (noteTitle, noteContent) => {
  const tempPage = {
    title: noteTitle,
    content: noteContent,
    project_id: _this.$store.getters.getProjects[0]?.id,
    getTeamID: _this.$store.getters.getTeamID,
  };
  browserStorageSetItem(quickPageNoteKey, tempPage);
  _this.$store.dispatch('setNoteInQuickPage', note.value?.id);
  _this.$store.dispatch('setQuickPage', true);
};

const openEpic = (card) => {
  const dashedTitle = getDashedTitle(card.title);
  _this.$router.push({
    name: constants.epicRoute.name,
    params: { cardId: card.id, cardTitle: dashedTitle },
    query: { cardOpenedFromNotes: true },
  });
};

const convertNoteToEpic = async (noteTitle, noteContent) => {
  if (!allowEpicInteraction()) return;

  let epics = _this.$store.getters.getFlatBoards[constants.epicsBoardId];
  if (!epics) {
    const { board } = await _this.$store.dispatch('fetchBoard', {
      boardId: constants.epicsBoardId,
    });
    epics = board;
  }

  _this.$store
    .dispatch('createCard', {
      card: { title: noteTitle, content: noteContent, list_id: epics.list_order?.[0] },
      type: ResourceType.Epic,
    })
    .then(({ card }) => {
      _this.$store.dispatch('addCardToCards', { card });
      _this.$store.dispatch('addCardToListCards', { cardId: card.id, listId: card.list_id });

      _this.$store.dispatch('addUiNotification', {
        message: translate('epicCreated'),
        status: constants.uiNotificationStatuses.info,
        duration: 4000,
        labels: [
          {
            text: translate('open'),
            type: constants.uiNotificationElements.button,
            callback: openEpic,
            params: card,
            buttonStyle: 'important',
          },
        ],
      });
    })
    .catch(() => {
      _this.$store.dispatch('addUiNotification', {
        message: translate(
          noteTitle.length === 0 ? 'epicMustHaveTitle' : 'roadmapMustHaveAtLeastOneStatus'
        ),
        status: constants.uiNotificationStatuses.error,
        duration: 4000,
      });
    });
};

const handleConvertToOptionClick = (option, hide) => {
  hide?.();
  if (showWarningToCreateNote()) return;

  const noteTitle = note.value?.title || '';

  switch (option.id) {
    case ResourceType.Card: {
      convertNoteToCard(noteTitle, noteContent.value);
      break;
    }
    case ResourceType.Page: {
      convertNoteToPage(noteTitle, noteContent.value);
      break;
    }
    case ResourceType.Epic: {
      convertNoteToEpic(noteTitle, noteContent.value);
      break;
    }
  }
};

const openNoteMeetingLink = () => {
  if (isAutoStartTranscriptionEnabled.value && isElectronApp.value) startRecording();

  const { link } = noteMeetingLinkInfo.value || {};

  if (link) {
    const url = link.match(/^https?:\/\//) ? link : `https://${link}`;
    window?.open(url, '_blank');
  }
};

const convertToOptions = [
  {
    id: ResourceType.Card,
    label: translate(ResourceType.Card),
    customIconName: iconPath('new-card-icon'),
  },
  {
    id: ResourceType.Epic,
    label: translate(ResourceType.Epic),
    customIconName: iconPath('box'),
    ...epicUpgradeBadge.value,
  },
  {
    id: ResourceType.Page,
    label: translate(ResourceType.Page),
    customIconName: iconPath('page'),
  },
];

const selectedTemplateLabel = computed(() => {
  return (
    noteTemplates.value[note.value?.last_note_template_id]?.title ||
    noteTemplatesFavourited.value[note.value?.last_note_template_id]?.title ||
    translate('deletedTemplate')
  );
});

const selectedTemplateIndex = computed(() => {
  return noteTemplatesOptions.value.findIndex(
    (template: any) => template.id === noteTemplateId.value
  );
});

const publicNoteUrl = computed(() => getPublicNoteUrl(props.noteId));

const togglePublicNoteText = computed(() => {
  return translate(publicNoteUrl.value ? 'shared' : 'share');
});

const togglePublicNoteIconColor = computed(() => {
  return publicNoteUrl.value ? 'var(--highlightInline)' : 'var(--iconDefault)';
});

const noteMeetingLinkInfo = computed(() => getNoteMeetingLinkInfo(props.noteId));

const suggestedActionsButtonText = computed(() => {
  const numberOfSuggestionsText = numberOfSuggestions.value
    ? `${numberOfSuggestions.value} ${translate('suggestedActions').toLowerCase()}`
    : translate('noSuggestedActions');
  return isGeneratingActions.value
    ? translate('generatingSuggestedActions')
    : numberOfSuggestionsText;
});

const joinNoteMeetingText = computed(() => {
  const meetingTime = convertToHoursMinutes(note.value.meeting_time);

  return translate('joinMeetingFromNote', {
    provider: noteMeetingLinkInfo.value.provider,
    time: meetingTime,
  });
});

const showNoteMeetingLink = computed(() => {
  const isMeetingToday = isToday(note.value?.meeting_time * 1000);
  return noteMeetingLinkInfo.value.link && isMeetingToday;
});

const handleEsc = (event) => {
  if (event.key === 'Escape') closeNote();
};

const handleSuggestedActionsClick = () => {
  EventBus.$emit('note:toggleSuggestedActions');
};

const closeSuggestedActionsSidebar = () => {
  EventBus.$emit('note:toggleSuggestedActions', false);
};

const updateNoteTemplate = (templateId: NoteTemplate) => {
  noteTemplateId.value = templateId;
};

watch(
  toRef(props, 'noteId'),
  async () => {
    /**
     * Change to last note template id if it exists,
     * otherwise switch to my notes tab and show user notes.
     * Switching notes will not update the last note template id.
     */
    const lastNoteTemplateId = getLastNoteTemplate(props.noteId);

    if (!lastNoteTemplateId) {
      noteTabClick(NoteTabId.Original, false);
    } else {
      applyTemplate({ id: lastNoteTemplateId }, false);

      const suggestedActions = await store.dispatch('getNoteSuggestions', {
        noteId: props.noteId,
        aiNoteId: aiNote.value.id,
      });

      if (suggestedActions?.length > 0 && !globalState.showSuggestedActions) {
        handleSuggestedActionsClick();
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  fetchNoteTemplates();

  window.addEventListener('keydown', handleEsc);

  EventBus.$on('note:switchToTab', switchToTab);
  EventBus.$on('note:updateNoteTemplate', updateNoteTemplate);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEsc);

  EventBus.$off('note:switchToTab', switchToTab);
  EventBus.$off('note:updateNoteTemplate', updateNoteTemplate);
});
</script>

<style lang="scss">
.note-editor-bar {
  border-bottom-style: solid;
  max-height: $noteEditorBarHeight;
  @apply bg-background-strongest flex gap-2 p-4 border-b-[1px] border-standard-border h-fit;

  .right-side {
    @apply ml-auto flex items-center gap-2;
  }

  .ai-note-generating {
    @apply pointer-events-none opacity-50;
  }

  .note-template-button {
    .button-icon {
      @apply mr-0 ml-1;
    }
  }

  .note-editor-tabs {
    .tab-item-icon {
      @apply flex-shrink-0;
    }
  }

  .note-editor-expand {
    .square-icon-wrap {
      @apply h-6 w-6;
    }

    .centerIcon {
      em {
        @apply flex items-center w-4 h-4;
      }
    }
  }
}

.disabled-button {
  background: var(--inactiveElementBg) !important;
  border: 1px solid var(--inactiveElementBorder) !important;

  .button-label {
    color: var(--placeholderText);
  }

  .button-icon {
    @apply bg-inactive-icon;
  }
}

.highlighted-share-button {
  .button-label {
    @apply text-highlight-inline;
  }
}

.share-secondary-regular {
  .button-icon {
    margin-right: 4px !important;
  }
}
</style>
